// Build recipes: what a built avatar is made of, stored so it can be reopened
// in the builder and edited.
//
// The recipe lives in two places:
//   - the avatars row: creation_params.builder ('v1') + builder_selection (JSON
//     of the BuilderSelection, base included) + builder_recipe_v
//   - the VRM itself: asset.extras.gxBuilt ({ v, ...selection }), written by
//     BuilderKit.assemble, as a fallback when the row has lost it
// Both are run through normalizeSelection on the way back in, so recipes saved
// before a part was added (or after one was retired) still open.
// v2 (garment library): selections may carry `lib` (layered garments by slot).
// normalizeLibrary drops unknown / retired library ids, wrong slots, bad
// colours and excluded pairs, and v1 recipes (no `lib`, old v2 garment ids in
// top / bottom / shoes / over) open exactly as before.
import { readGlb } from './glb';
import { normalizeSelection, type BuilderSelection } from './catalog';

export const BUILDER_RECIPE_VERSION = 2;

type Params = Record<string, string> | null | undefined;

/** creation_params entries that record a build (strings only). */
export function recipeParams(sel: BuilderSelection): Record<string, string> {
  return { builder: 'v1', builder_recipe_v: String(BUILDER_RECIPE_VERSION), builder_selection: JSON.stringify(sel) };
}

/** Was this avatar made in the builder? */
export function isBuiltAvatar(params: Params): boolean {
  return !!params?.builder;
}

/** The recipe saved on the avatar row, or null if there is none / it is unreadable. */
export function recipeFromParams(params: Params): BuilderSelection | null {
  const raw = params?.builder_selection;
  if (!raw) return null;
  try {
    const s = JSON.parse(raw);
    return s && typeof s === 'object' ? normalizeSelection(s) : null;
  } catch {
    return null;
  }
}

/** The recipe embedded in a built .vrm (asset.extras.gxBuilt), or null. */
export function recipeFromVrm(bytes: Uint8Array): BuilderSelection | null {
  try {
    const built = readGlb(bytes).json?.asset?.extras?.gxBuilt;
    if (!built || typeof built !== 'object') return null;
    const sel = { ...built };
    delete sel.v;
    return normalizeSelection(sel);
  } catch {
    return null;
  }
}

/** Row recipe first, else download the model and read the embedded one. */
export async function loadRecipe(avatar: { creation_params: Params; model_url: string }): Promise<BuilderSelection | null> {
  const fromRow = recipeFromParams(avatar.creation_params);
  if (fromRow) return fromRow;
  try {
    const r = await fetch(avatar.model_url);
    if (!r.ok) return null;
    return recipeFromVrm(new Uint8Array(await r.arrayBuffer()));
  } catch {
    return null;
  }
}
